<template>
  <ToolTip :content="partnerTooltip">
    <div
      v-if="canInvite"
      class="members__new"
    >
      <div class="members__new__header">Invite members</div>

      <div
        v-if="isSSOEnforced"
        class="members__new__description"
      >
        Invites are not required when SSO login is enforced. Users can log in directly using SSO.
      </div>
      <div
        v-else
        class="members__new__description"
      >
        Invite new members to your team
      </div>

      <div
        v-if="!isSSOEnforced"
        class="members__new__form"
      >
        <InputField
          v-model="newInvitationEmail"
          :disabled="isPartnerMember"
          :error="error.email"
          placeholder="Email address (Press Enter to send the invite)"
          @enter="createInvitation"
        />

        <RoleDropdown
          class="members__new__role"
          :value="newInvitationRole"
          name="role"
          placeholder="Role"
          :options="invitationRoleOptions"
          :disabled="isPartnerMember"
          @input="newInvitationRole = $event"
        />

        <IconButton
          flair="rounded"
          kind="primary"
          size="md"
          :disabled="isPartnerMember"
          @click="createInvitation"
        >
          <Icon
            name="duotone-send"
            class="members__new__icon"
          />
        </IconButton>
      </div>
    </div>
  </ToolTip>
</template>

<script lang="ts">
export default {
  name: 'InviteForm',
}
</script>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import InputField from '@/components/Common/InputField/V2/InputField.vue'
import RoleDropdown from '@/modules/Teams/RoleDropdown.vue'
import { useAuth } from '@/modules/Auth/useAuth'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { MembershipRole } from '@/store/types'
import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'

import { getInvitationRoleOptions } from '@/modules/Teams/roleData'
import { useUserStore } from '@/modules/Auth/useUserStore'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import { useFeatureFlagsStore } from '@/pinia/useFeatureFlagsStore'
import { useInviteStore } from '@/modules/Teams/useInviteStore'
import { assert } from '@/core/utils/assert'

type InvitationError = {
  email?: string
}

const teamStore = useTeamStore()

const { isAuthorized } = useAuth()
const invitationRoleOptions = computed(() => getInvitationRoleOptions(isAuthorized))

const canInvite = computed(() => invitationRoleOptions.value.length > 0)

const userStore = useUserStore()
const flagStore = useFeatureFlagsStore()
const inviteStore = useInviteStore()

const isPartnerMember = computed(() => {
  // Workspace teams treat child teams as Workspaces
  // and product defines that their users should be able to invite new members
  //
  // Workspace teams and legacy BPO teams reuse the same
  // data field for the relation between partner <-> client under the hood
  // so this check is needed to differentiate between the 2 cases
  // and the feature flag is the only way to do it for now
  if (flagStore.featureFlags.WORKSPACE_PRICING) {
    return false
  }

  const currentTeam = assert(teamStore.currentTeam)
  const currentUser = assert(userStore.currentUser)

  // current team is not client, so the whole concept doesn't apply
  if (currentTeam.managed_status !== 'client') {
    return false
  }

  // current team is client and user it direct member of client
  if (teamStore.currentTeamMemberships.some((m) => m.user_id === currentUser.id)) {
    return false
  }

  // current team is client and user is direct member of partner
  return teamStore.allRelevantMemberships.some(
    (m) => m.team_id === currentTeam.partner_id && m.user_id === currentUser.id,
  )
})

const isSSOEnforced = computed<boolean>(() => !!teamStore.currentTeam?.sso_enforced)

const partnerTooltip = computed(() => {
  if (!isPartnerMember.value) {
    return
  }

  return 'You are not allowed to add users directly to this team'
})

const newInvitationRole = ref<MembershipRole>('member')
const newInvitationEmail = ref<string>('')

onMounted(() => {
  if (invitationRoleOptions.value.length > 0) {
    newInvitationRole.value = invitationRoleOptions.value[0].id
  }
})

const error = ref<InvitationError>({})

const createInvitation = async (): Promise<void> => {
  if (newInvitationEmail.value.length === 0 || !teamStore.currentTeam) {
    return
  }

  const { success, email } = await inviteStore.createInvitations(teamStore.currentTeam.slug, [
    { email: newInvitationEmail.value, role: newInvitationRole.value },
  ])

  if (success) {
    newInvitationEmail.value = ''
    error.value = {}
    return
  }

  if (email) {
    error.value = { email }
    return
  }
}
</script>

<style lang="scss" scoped>
.members__new {
  @include col;
  border-radius: 16px;
  background-color: var(--color-surface-tertiary);
  padding: 8px;
}

.members__new__header {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-top: 4px;
  margin-left: 12px;
}

.members__new__description {
  @include typographyRegularBody200;
  margin-bottom: 4px;
  margin-left: 12px;
  color: var(--color-text-subtlest);
}

.members__new__form {
  @include row;
  border-radius: 14px;
  background-color: var(--color-surface-default);
  gap: 14px;
  margin-top: 8px;
  padding: 8px;
}

.members__new__role {
  width: 240px;
}

.members__new__icon {
  font-size: 20px;
}
</style>
